import * as React from "react";
import moment from "moment";
import impiricaLogo from "src/assets/report_images/Impirica_logo_large.png";
import { uniqBy } from "lodash";
import largeVehicle from "src/assets/report_images/large_vehicle.jpg";
import lightVehicle from "src/assets/report_images/light_vehicle.jpg";
import { Icon } from "src/components/icons/Icon";
import { Circle, CircleAlert, CircleCheck, CircleX } from "lucide-react";
import { HalfCircle, HospitalSymbol } from "src/components/icons/custom";
import { vitalsConstant } from "src/admin/report/templates/v1/constants/VitalsConstants";
import Utils from "src/common/utils";
import PickListContext from "src/context/pickListContext";
import { useContext } from "react";

// refactor entire page into components when have time
enum CategoryAssessment {
	fail = 0,
	borderline = 1,
	pass = 2,
	hazardous = 3,
}

interface IProps {
	report: ReportNecessaryInfoDTO;
	reportSetting: ReportSettingDTO;
	locations: LocationDTO[];
	coreTestResult: CoreTestResultDTO;
	coreErrorEntries: CoreErrorEntryDTO[];
	vitalsTestResult: VitalsTestResultDTO;
	vitalsToplots: VitalsToplotsDTO;
	vitalsSubScores: VitalsSubScoresDTO;
	vitalsCompletedTasks: VitalsCompletedTasksDTO[];
}

const defaultReportSetting = {
	primaryColor: "#09324c",
	secondaryColor: "#e06f23",
	logoUrl: "",
	excelerateReplacementText: "ExceleRATE",
	vitalsReplacementText: "VITALS",
	coreReplacementText: "CORE",
	disclaimer: "",
};

const FullReportTemplate: React.FunctionComponent<IProps> = (props) => {
	const {
		report,
		reportSetting = {},
		coreTestResult,
		coreErrorEntries,
		vitalsTestResult,
		vitalsSubScores,
		vitalsCompletedTasks,
	} = props;

	const { getPickListItemDescription, pickListItems } =
		useContext(PickListContext);

	for (const key of Object.keys(defaultReportSetting)) {
		if (!reportSetting[key]) {
			reportSetting[key] = defaultReportSetting[key];
		}
	}

	// If white labelling is disabled ensure defaults are used
	if (reportSetting.whiteLabel === false) {
		reportSetting.primaryColor = defaultReportSetting.primaryColor;
		reportSetting.secondaryColor = defaultReportSetting.secondaryColor;
		reportSetting.excelerateReplacementText =
			defaultReportSetting.excelerateReplacementText;
		reportSetting.vitalsReplacementText =
			defaultReportSetting.vitalsReplacementText;
		reportSetting.coreReplacementText =
			defaultReportSetting.coreReplacementText;
		reportSetting.disclaimer = defaultReportSetting.disclaimer;
	}

	if (reportSetting.showLogo === false) {
		reportSetting.logoUrl = defaultReportSetting.logoUrl;
	}

	const {
		primaryColor,
		secondaryColor,
		logoUrl,
		excelerateReplacementText,
		vitalsReplacementText,
		coreReplacementText,

		disclaimer,
	} = reportSetting;

	const passIcon = (
		<Icon icon={CircleCheck} badge size="2x" style={{ color: "#027F01" }} />
	);
	const indeterminateIcon = (
		<Icon icon={CircleAlert} badge size="2x" style={{ color: "#FCA900" }} />
	);
	const failIcon = (
		<Icon icon={CircleX} badge size="2x" style={{ color: "#FA0B02" }} />
	);
	const hazardouseIcon = (
		<Icon icon={HospitalSymbol} badge size="2x" style={{ color: "#FA0B02" }} />
	);

	const generateCoreCustomFieldRows = () => {
		if (
			coreTestResult.coreTestCustomFieldValues &&
			coreTestResult.coreTestCustomFieldValues.length > 0
		) {
			const renderedCoreCustomFieldNameAndValues =
				coreTestResult.coreTestCustomFieldValues.map((customField) => (
					<p key={customField.name}>
						{customField.name}: <b>{`%cf-${customField.name}%`}</b>
					</p>
				));
			return (
				<React.Fragment>
					<div className="core-grey-block avoid-page-break top-border text-break">
						<h4 className="core-grey-block-header">
							{coreReplacementText} Test Additional Data
						</h4>
						{renderedCoreCustomFieldNameAndValues}
					</div>
				</React.Fragment>
			);
		} else {
			return;
		}
	};

	const generateVitalsCustomFieldRows = () => {
		if (
			vitalsTestResult.vitalsCustomFieldValues &&
			vitalsTestResult.vitalsCustomFieldValues.length > 0
		) {
			const renderedVitalsCustomFields =
				vitalsTestResult.vitalsCustomFieldValues.map((customField) => (
					<p key={customField.name}>
						{customField.name}: <b>{`%cf-${customField.name}%`}</b>
					</p>
				));
			return (
				<React.Fragment>
					<div className="core-grey-block avoid-page-break top-border text-break">
						<h4 className="core-grey-block-header">
							{vitalsReplacementText} Test Additional Data
						</h4>
						{renderedVitalsCustomFields}
					</div>
				</React.Fragment>
			);
		} else {
			return null;
		}
	};

	const generateClientCustomFieldRows = () => {
		// refactor entire page to components
		if (report.clientCustomFields && report.clientCustomFields.length > 0) {
			return report.clientCustomFields.map((customFieldValue) => {
				return (
					<tr key={customFieldValue.id}>
						<td className="core-row text-nowrap">{customFieldValue.name}: </td>
						<td className="core-row text-nowrap" colSpan={3}>
							{`%cf-${customFieldValue.name}%`}
						</td>
					</tr>
				);
			});
		} else {
			return null;
		}
	};

	const generateAssessmentCustomFieldRows = () => {
		if (
			report.assessmentCustomFields &&
			report.assessmentCustomFields.length > 0
		) {
			return report.assessmentCustomFields.map((customFieldValue) => {
				return (
					<tr key={customFieldValue.id}>
						<td className="core-row text-nowrap">{customFieldValue.name}: </td>
						<td className="core-row text-nowrap" colSpan={3}>
							{`%cf-${customFieldValue.name}%`}
						</td>
					</tr>
				);
			});
		} else {
			return null;
		}
	};
	// create function to render custom fields for assessment and vitals

	const generateSkillSummary = () => {
		const selectedSkillCategories = coreErrorEntries.filter(
			(entry) => entry.values!.length > 0
		);
		if (selectedSkillCategories.length === 0) {
			return (
				<ul>
					<li>No driving errors applicable.</li>
				</ul>
			);
		}
		const groupCategories = uniqBy(
			selectedSkillCategories,
			(x) => x.skillCategoryId
		);
		const regex = /[A-Z].*/g;
		return (
			<ul>
				{groupCategories.map((category) => (
					<li key={Utils.generateUUID()}>
						{getPickListItemDescription(category.skillCategoryId).match(regex)}
					</li>
				))}
			</ul>
		);
	};

	const generateErrorSummary = () => {
		const selectedSkillCategories = coreErrorEntries.filter(
			(entry) => entry.values!.length > 0
		);
		if (selectedSkillCategories.length === 0) {
			return (
				<ul>
					<li>No driving errors applicable.</li>
				</ul>
			);
		}
		const errorDescriptions: string[] = [];
		selectedSkillCategories.forEach((entry) => {
			entry.values!.forEach((value) => {
				errorDescriptions.push(value.description!);
			});
		});
		return (
			<ul>
				{errorDescriptions.map((description) => (
					<li key={Utils.generateUUID()}>{description}</li>
				))}
			</ul>
		);
	};

	const generateResultBasedOnId = (resultId: number) => {
		if (resultId === CategoryAssessment.fail) {
			return "Fail";
		} else if (resultId === CategoryAssessment.borderline) {
			return "Borderline";
		} else if (resultId === CategoryAssessment.pass) {
			return "Pass";
		} else {
			return "Hazardous";
		}
	};

	const generateIconForErrors = (resultId: number) => {
		let icon = <React.Fragment>-</React.Fragment>;
		if (resultId !== undefined) {
			const iconType = generateResultBasedOnId(resultId);
			if (iconType === "Fail") {
				icon = (
					<Icon icon={CircleX} badge size="2x" style={{ color: "#FA0B02" }} />
				);
			}

			if (iconType === "Borderline") {
				icon = (
					<Icon
						icon={CircleAlert}
						badge
						size="2x"
						style={{ color: "#FCA900" }}
					/>
				);
			}

			if (iconType === "Pass") {
				icon = (
					<Icon
						icon={CircleCheck}
						badge
						size="2x"
						style={{ color: "#027F01" }}
					/>
				);
			}

			if (iconType === "Hazardous") {
				icon = (
					<Icon
						icon={HospitalSymbol}
						badge
						size="2x"
						style={{ color: "#FA0B02" }}
					/>
				);
			}
			return icon;
		}

		return icon;
	};

	const createCategoryDescriptionBasedOnIconTypeAndAssessment = (
		statusType: string
	) => {
		let categoryDescription = ``;
		if (statusType === "cErrorStatusId") {
			categoryDescription = `These errors are generally more hazardous in nature and are more
			prevalent amongst drivers who have demonstrated cognitive deficit.`;
		}

		if (statusType === "tErrorStatusId") {
			categoryDescription = `Trainable errors are bad habit errors that are common amongst drivers of all experience levels.`;
		}

		if (statusType === "appraisalStatusId") {
			categoryDescription = `This appraisal represents the opinion of the on-road evaluator and identifies issues
			that were not necessarily captured as competence defining or trainable errors.`;
		}

		return categoryDescription;
	};

	const showCoreScore = (coreScore: any) => {
		if (coreScore) {
			return (
				<tr>
					<td className="score-td-size">
						<div
							className="core-score-board"
							style={{ backgroundColor: primaryColor }}
						>
							<h1 className="core-score-text">{coreScore}/100</h1>
						</div>
					</td>
					<td className="d-flex align-items-center ml-2 align-text-helper">
						<div>
							<p>
								The {coreReplacementText} Score is a weighted measure out of 100
								comprising of three key components which are assessed during an
								on-road evaluation. The lower the score the better the
								performance.
							</p>
						</div>
					</td>
				</tr>
			);
		}

		return "";
	};

	const generateCategoryDescription = (resultId: number, type: string) => {
		let categoryDescription = ``;
		if (resultId !== undefined) {
			categoryDescription =
				createCategoryDescriptionBasedOnIconTypeAndAssessment(type);
			return categoryDescription;
		}
		return categoryDescription;
	};

	const checkIfCErrorIsHazardous = (resultId: number) => {
		const isHazardous = generateResultBasedOnId(resultId);
		if (isHazardous === "Hazardous") {
			return true;
		}

		return false;
	};

	const setHazardousErrorIcon = (resultId: number) => {
		const icon = "Ø";
		if (resultId !== undefined) {
			const isHazardous = checkIfCErrorIsHazardous(resultId);
			if (isHazardous) {
				return (
					<span className="mr-3">
						<Icon
							icon={HospitalSymbol}
							badge
							size="1x"
							style={{ color: "#FA0B02" }}
						/>
					</span>
				);
			}
		}
		return icon;
	};

	const setAbortedErrorIcon = (coreAborted: boolean) => {
		const icon = "Ø";
		if (coreAborted !== undefined) {
			if (coreAborted) {
				return (
					<span className="mr-3">
						<Icon
							icon={HospitalSymbol}
							badge
							size="1x"
							style={{ color: "#FA0B02" }}
						/>
					</span>
				);
			}
		}
		return icon;
	};

	const setVitalsErrorReccomendation = (type: string) => {
		if (type === "Fail" || type === "Hazardous") {
			return `Candidate presents a risk of having one or more medical conditions compromising their cognitive faculties required to to drive safely.
					Candidate should be screened by a medical practitioner to identify potential medical conditions that have impacted driving ability.`;
		}
		if (type === "Borderline") {
			return `Candidate presents minor to moderate risk of cognitive deficits that could compromise their ability to drive safely.
					No immediate action is required. For moderate risk, it is recommended to reevaluate within 6-12 months to identify potential changes.`;
		}

		return `Candidate presents limited to no risk of cognitive deficits that could compromise their ability to drive safely. No action required`;
	};

	const setCErrorReccomendation = (type: string) => {
		if (type === "Fail" || type === "Borderline" || type === "Hazardous") {
			return `Where hazardous Error and/or an Abort is identified, there is a higher likelihood that remediation will not be achieved by
					training alone and it is recommended that a medical review by a physician be performed followed by driver monitoring and training programs.`;
		}

		return `No action required`;
	};

	const setTErrorReccomendation = (type: string) => {
		if (type === "Fail" || type === "Borderline" || type === "Hazardous") {
			return `Review the specific areas for development identified in the ExceleRATE report
					and remediate by way of thorough driver training and monitoring program.`;
		}

		return `No action required`;
	};

	const setSkillAppraisalReccomendataion = (type: string) => {
		if (type === "Fail" || type === "Borderline" || type === "Hazardous") {
			return `Use the information to further develop the remediation plan that was established from the two other on-road components.
					Where discrepancies exist, follow up with the on-road evaluator to obtain greater context.`;
		}

		if (type === "Pass") {
			return `No action required`;
		}

		return ``;
	};

	const createRecommendationBasedOnIconTypeAndAssessment = (
		iconType: string,
		statusType: string
	) => {
		let recommendationDescription = ``;
		if (statusType === "vitalsStatusId") {
			if (iconType === "Fail" || iconType === "Hazardous") {
				recommendationDescription = setVitalsErrorReccomendation("Fail");
			}

			if (iconType === "Borderline") {
				recommendationDescription = setVitalsErrorReccomendation("Borderline");
			}

			if (iconType === "Pass") {
				recommendationDescription = setVitalsErrorReccomendation("Pass");
			}
			return recommendationDescription;
		}

		if (statusType === "cErrorStatusId") {
			if (
				iconType === "Fail" ||
				iconType === "Borderline" ||
				iconType === "Hazardous"
			) {
				recommendationDescription = setCErrorReccomendation("Fail");
			}

			if (iconType === "Pass") {
				recommendationDescription = setCErrorReccomendation("Pass");
			}
			return recommendationDescription;
		}

		if (statusType === "tErrorStatusId") {
			if (
				iconType === "Fail" ||
				iconType === "Borderline" ||
				iconType === "Hazardous"
			) {
				recommendationDescription = setTErrorReccomendation("Fail");
			}

			if (iconType === "Pass") {
				recommendationDescription = setTErrorReccomendation("Pass");
			}

			return recommendationDescription;
		}

		if (statusType === "appraisalStatusId") {
			if (
				iconType === "Fail" ||
				iconType === "Borderline" ||
				iconType === "Hazardous"
			) {
				recommendationDescription = setSkillAppraisalReccomendataion("Fail");
			}

			if (iconType === "Pass") {
				recommendationDescription = setSkillAppraisalReccomendataion("Pass");
			}

			return recommendationDescription;
		}

		return recommendationDescription;
	};

	const generateRecommendation = (resultId: number, type: string) => {
		let recommendationDescription = ``;
		if (resultId !== undefined) {
			const iconType = generateResultBasedOnId(resultId);
			recommendationDescription =
				createRecommendationBasedOnIconTypeAndAssessment(iconType, type);
			return recommendationDescription;
		}
		return recommendationDescription;
	};

	// render cognitive functions assessed Icons functions
	const belowAverageIcon = <div className="red-arrow-down" />;
	const averageIcon = <div className="black-square" />;
	const aboveAverageIcon = <div className="green-arrow-up" />;

	const renderIconForResponseToStimuli = (
		vitalsSubScore: VitalsSubScoresDTO
	) => {
		const { value6, value7, value13 } = vitalsSubScore;
		if (
			value13! > vitalsConstant.VALUES_ENCODE_BELOW_AVERAGE_ONE ||
			value7! > vitalsConstant.VALUES_ENCODE_BELOW_AVERAGE_TWO ||
			value6! > vitalsConstant.VALUES_ENCODE_BELOW_AVERAGE_THREE
		) {
			return belowAverageIcon;
		}

		if (
			(value13! < vitalsConstant.VALUES_ENCODE_BELOW_AVERAGE_ONE &&
				value13! > vitalsConstant.VALUES_ENCODE_AVERAGE_ONE) ||
			(value7! > vitalsConstant.VALUES_ENCODE_AVERAGE_TWO &&
				value7! < vitalsConstant.VALUES_ENCODE_AVERAGE_TWO) ||
			(value6! > vitalsConstant.VALUES_ENCODE_AVERAGE_THREE &&
				value6! < vitalsConstant.VALUES_ENCODE_BELOW_AVERAGE_THREE)
		) {
			return averageIcon;
		}

		return aboveAverageIcon;
	};

	const renderIconForSpatialRelations = (
		vitalsSubScore: VitalsSubScoresDTO
	) => {
		const { value15 } = vitalsSubScore;
		if (value15! > vitalsConstant.VALUES_SPATIAL_BELOW_AVERAGE_ONE) {
			return belowAverageIcon;
		}

		if (
			value15! < vitalsConstant.VALUES_SPATIAL_BELOW_AVERAGE_ONE &&
			value15! > vitalsConstant.VALUES_SPATIAL_AVERAGE_ONE
		) {
			return averageIcon;
		}

		return aboveAverageIcon;
	};

	const renderIconForDecisionTimePressure = (
		vitalsSubScore: VitalsSubScoresDTO
	) => {
		const { value16 } = vitalsSubScore;
		if (value16! > vitalsConstant.VALUES_DECISIONS_BELOW_AVERAGE_ONE) {
			return belowAverageIcon;
		}

		if (
			value16! > vitalsConstant.VALUES_DECISIONS_AVERAGE_ONE &&
			value16! < vitalsConstant.VALUES_DECISIONS_BELOW_AVERAGE_ONE
		) {
			return averageIcon;
		}

		return aboveAverageIcon;
	};

	const renderIconForGuidedMovementControl = (
		vitalsSubScore: VitalsSubScoresDTO
	) => {
		const { value2 } = vitalsSubScore;
		if (value2! > vitalsConstant.VALUES_MOVEMENT_BELOW_AVERAGE_ONE) {
			return belowAverageIcon;
		}

		if (
			value2! < vitalsConstant.VALUES_MOVEMENT_BELOW_AVERAGE_ONE &&
			value2! > vitalsConstant.VALUES_MOVEMENT_AVERAGE_ONE
		) {
			return averageIcon;
		}

		return aboveAverageIcon;
	};

	const renderIconForComplexInformation = (
		vitalsSubScore: VitalsSubScoresDTO
	) => {
		const { value21 } = vitalsSubScore;
		if (value21! > vitalsConstant.VALUES_INFORMATION_BELOW_AVERAGE_ONE) {
			return belowAverageIcon;
		}

		if (
			value21! < vitalsConstant.VALUES_INFORMATION_BELOW_AVERAGE_ONE &&
			value21! > vitalsConstant.VALUES_INFORMATION_AVERAGE_ONE
		) {
			return averageIcon;
		}

		return aboveAverageIcon;
	};

	const renderIconForReactions = (vitalsSubScore: VitalsSubScoresDTO) => {
		const { value1, value17 } = vitalsSubScore;
		if (
			value1! > vitalsConstant.VALUES_REACTIONS_BELOW_AVERAGE_ONE ||
			value17! < vitalsConstant.VALUES_REACTIONS_BELOW_AVERAGE_TWO
		) {
			return belowAverageIcon;
		}

		if (
			(value1! < vitalsConstant.VALUES_REACTIONS_BELOW_AVERAGE_ONE &&
				value1! > vitalsConstant.VALUES_REACTIONS_AVERAGE_ONE) ||
			(value17! > vitalsConstant.VALUES_REACTIONS_BELOW_AVERAGE_TWO &&
				value17! < vitalsConstant.VALUES_REACTIONS_AVERAGE_TWO)
		) {
			return averageIcon;
		}

		return aboveAverageIcon;
	};

	const renderIconForFineMotorControl = (
		vitalsSubScore: VitalsSubScoresDTO
	) => {
		const { value5 } = vitalsSubScore;
		if (value5! > vitalsConstant.VALUES_MOTOR_BELOW_AVERAGE_ONE) {
			return belowAverageIcon;
		}

		if (
			value5! < vitalsConstant.VALUES_MOTOR_BELOW_AVERAGE_ONE &&
			value5! > vitalsConstant.VALUES_MOTOR_AVERAGE_ONE
		) {
			return averageIcon;
		}

		return aboveAverageIcon;
	};

	const renderIconForProcessVisualEvents = (
		vitalsSubScore: VitalsSubScoresDTO
	) => {
		const { value12, value18, value19 } = vitalsSubScore;
		if (
			value12! > vitalsConstant.VALUES_VISUAL_BELOW_AVERAGE_ONE ||
			value18! < vitalsConstant.VALUES_VISUAL_BELOW_AVERAGE_TWO ||
			value19! > vitalsConstant.VALUES_VISUAL_BELOW_AVERAGE_THREE
		) {
			return belowAverageIcon;
		}

		if (
			value12 === vitalsConstant.VALUES_VISUAL_BELOW_AVERAGE_ONE ||
			(value18! > vitalsConstant.VALUES_VISUAL_BELOW_AVERAGE_TWO &&
				value18! < vitalsConstant.VALUES_VISUAL_BELOW_AVERAGE_ONE) ||
			(value19! < vitalsConstant.VALUES_VISUAL_BELOW_AVERAGE_THREE &&
				value19! > vitalsConstant.VALUES_VISUAL_AVERAGE_ONE)
		) {
			return averageIcon;
		}

		return aboveAverageIcon;
	};

	const renderIconForImpluseControl = (vitalsSubScore: VitalsSubScoresDTO) => {
		const { value8 } = vitalsSubScore;
		if (value8! > vitalsConstant.VALUES_IMPULSE_BELOW_AVERAGE_ONE) {
			return belowAverageIcon;
		}

		if (
			value8! < vitalsConstant.VALUES_IMPULSE_BELOW_AVERAGE_ONE &&
			value8! > vitalsConstant.VALUES_IMPULSE_AVERAGE_ONE
		) {
			return averageIcon;
		}

		return aboveAverageIcon;
	};

	const renderCompletedTaskIcons = (
		completedTasks: VitalsCompletedTasksDTO[]
	) => {
		let reactionIcons: any = null;
		let focusIcons: any = null;
		let spatialIcons: any = null;
		let attentionShiftingIcons: any = null;
		let memoryControlIcons: any = null;
		let drivingSituationsIcons: any = null;

		const countOfMemoryControlTasks = completedTasks.filter(
			(task) => task.type === "swm"
		).length;
		const countOfFocusAndAttentionTasks = completedTasks.filter(
			(task) => task.type === "ufv"
		).length;
		const countOfAttentionShiftingTasks = completedTasks.filter(
			(task) => task.type === "attn"
		).length;
		const renderGapIcon = completedTasks.find((task) => task.type === "gap");
		const renderDrivingIcons = completedTasks.find(
			(task) => task.type === "vid"
		);
		const renderReactionIcons = completedTasks.find(
			(task) => task.type === "rt"
		);
		const completedIcon = (
			<Icon icon={Circle} filled size="2x" style={{ color: "#ffffff" }} />
		);
		const partiallyCompletedIcon = (
			<Icon icon={HalfCircle} size="2x" style={{ color: "#ffffff" }} />
		);
		const notCompletedIcon = (
			<Icon icon={Circle} size="2x" style={{ color: "#ffffff" }} />
		);

		// Task 1 RT
		if (renderReactionIcons === undefined) {
			reactionIcons = notCompletedIcon;
		} else {
			reactionIcons = completedIcon;
		}

		// Task 2 UFV
		if (countOfFocusAndAttentionTasks === 2) {
			focusIcons = completedIcon;
		} else if (countOfFocusAndAttentionTasks === 1) {
			focusIcons = partiallyCompletedIcon;
		} else {
			focusIcons = notCompletedIcon;
		}

		// Task 3 GAP
		if (renderGapIcon === undefined) {
			spatialIcons = notCompletedIcon;
		} else {
			spatialIcons = completedIcon;
		}

		// Task 4 ATTN
		if (countOfAttentionShiftingTasks === 2) {
			attentionShiftingIcons = completedIcon;
		} else if (countOfAttentionShiftingTasks === 1) {
			attentionShiftingIcons = partiallyCompletedIcon;
		} else {
			attentionShiftingIcons = notCompletedIcon;
		}

		// Task 5 SWM
		if (countOfMemoryControlTasks === 2) {
			memoryControlIcons = completedIcon;
		} else if (countOfMemoryControlTasks === 1) {
			memoryControlIcons = partiallyCompletedIcon;
		} else {
			memoryControlIcons = notCompletedIcon;
		}

		// Task 6 VIDEO
		if (renderDrivingIcons === undefined) {
			drivingSituationsIcons = notCompletedIcon;
		} else {
			drivingSituationsIcons = completedIcon;
		}

		const mutipleTaskIcons = [
			{ taskType: "rt", icon: reactionIcons },
			{ taskType: "ufv", icon: focusIcons },
			{ taskType: "gap", icon: spatialIcons },
			{ taskType: "attn", icon: attentionShiftingIcons },
			{ taskType: "swm", icon: memoryControlIcons },
			{ taskType: "vid", icon: drivingSituationsIcons },
		];

		const allIcons = [...mutipleTaskIcons];

		return allIcons.map((iconObject) => (
			<td
				key={iconObject.taskType}
				className="performance-summary-td performance-summary-center"
			>
				<strong>{iconObject.icon}</strong>
			</td>
		));
	};

	const renderVitalsScore = (vitalsStatusId: number) => {
		if (vitalsStatusId === CategoryAssessment.borderline) {
			return indeterminateIcon;
		} else if (vitalsStatusId === CategoryAssessment.fail) {
			return failIcon;
		} else if (vitalsStatusId === CategoryAssessment.hazardous) {
			return hazardouseIcon;
		} else {
			return passIcon;
		}
	};

	// render sections of components
	const renderClientSection = (reportInformation: ReportNecessaryInfoDTO) => {
		// Legacy codes were called web id and have a greater length
		const clientCodeText =
			report.clientCode!.length > 8 ? "Web Id:" : "Client Code:";

		return (
			<React.Fragment>
				<tr>
					<td className="core-row text-nowrap" colSpan={1}>
						Name:
					</td>
					<td className="core-row" colSpan={2}>
						{"%firstName%"} {"%lastName%"}
					</td>
				</tr>
				<tr>
					<td className="core-row text-nowrap" colSpan={1}>
						Employee Id:
					</td>
					<td className="core-row" colSpan={2}>
						{reportInformation.employeeId}
					</td>
				</tr>
				<tr>
					<td className="core-row text-nowrap">Date of Birth:</td>
					<td className="core-row" colSpan={3}>
						{"%birthMonth%"} {"%birthDay%"} {"%birthYear%"}
					</td>
				</tr>
				{/*
				<tr>
					<td className="core-row text-nowrap" vertical-align="top">Entered On:</td>
					<td className="core-row" colSpan={3}>
						On: {formatDateAdminsitered(coreTest!)} / At: {location.address1}, {location.city}
						/ Phone: {location.phone}
						/ Fax: {location.fax === undefined || location.fax === '' ? location.phone : location.fax}
						/ Email: {location.email}
					</td>
				</tr>
				*/}
				<tr>
					<td className="core-row text-nowrap">{clientCodeText}</td>
					<td className="core-row" colSpan={3}>
						{report.clientCode}
					</td>
				</tr>
				{generateClientCustomFieldRows()}
				{generateAssessmentCustomFieldRows()}
			</React.Fragment>
		);
	};

	const renderExcelerateSection = (
		assessmentReport: ReportNecessaryInfoDTO
	) => {
		if (assessmentReport.vitalsScore && coreTestResult.dateCompleted!) {
			// assessment result values
			return (
				<div className="container full-width">
					<h1 className="score-header" style={{ color: secondaryColor }}>
						{excelerateReplacementText} OVERALL RESULTS
					</h1>
					<table className="full-width">
						<tbody>
							<tr>
								<td
									className="core-row evaluator-form-header"
									rowSpan={2}
									align="center"
									style={{ backgroundColor: secondaryColor, paddingTop: 1 }}
								>
									<h1 className="core-score-text">
										{assessmentReport.excelerateScore}/100
									</h1>
								</td>
								<td className="core-row" align="center">
									Measures
								</td>
								<td
									className="core-row core-row-hd"
									align="center"
									style={{ backgroundColor: primaryColor }}
								>
									{vitalsReplacementText} Score
								</td>
								<td
									className="core-row core-row-hd"
									align="center"
									style={{ backgroundColor: primaryColor }}
								>
									{coreReplacementText} C-Errors
								</td>
								<td
									className="core-row core-row-hd"
									align="center"
									style={{ backgroundColor: primaryColor }}
								>
									{coreReplacementText} T-Errors
								</td>
								<td
									className="core-row core-row-hd"
									align="center"
									style={{ backgroundColor: primaryColor }}
								>
									{coreReplacementText} Skills Appraisal
								</td>
								<td
									rowSpan={2}
									className="icon-group pl-4"
									align="left"
								>
									<Icon
										icon={CircleCheck}
										badge
										size="2x"
										style={{ color: "#027F01" }}
									/>
									<span> Above or Average Performance</span> <br />
									<Icon
										icon={CircleAlert}
										badge
										size="2x"
										style={{ color: "#FCA900" }}
									/>
									<span> Borderline Problems Detected</span> <br />
									<Icon
										icon={CircleX}
										badge
										size="2x"
										style={{ color: "#FA0B02" }}
									/>
									<span> Serious Problems Detected</span> <br />
									<Icon
										icon={HospitalSymbol}
										badge
										size="2x"
										style={{ color: "#FA0B02" }}
									/>
									<span> Hazardous Error Detected</span>
								</td>
							</tr>
							<tr>
								<td className="core-row" align="center">
									Results
								</td>
								<td className="core-row" align="center">
									{renderVitalsScore(report.vitalsStatusId!)}
								</td>
								<td className="core-row" align="center">
									{generateIconForErrors(report.cErrorStatusId!)}
								</td>
								<td className="core-row" align="center">
									{generateIconForErrors(report.tErrorStatusId!)}
								</td>
								<td className="core-row" align="center">
									{generateIconForErrors(report.appraisalStatusId!)}
								</td>
							</tr>
						</tbody>
					</table>
					<br />
					<p>
						<strong>
							The ExceleRATE Overall Result is a weighted measure out of 100 of
							four key components which includes an in-office cognitive
							assessment and an on-road evaluation. The lower the score the
							better the performance. The ExceleRATE score indicated in this
							section should be the primary result considered when evaluating
							the information contained in this report. The information that
							follows is intended to be used as a secondary source only.
						</strong>
					</p>
					<hr />
				</div>
			);
		} else {
			return;
		}
	};

	const renderVitalsSection = (
		assessmentResult: ReportNecessaryInfoDTO,
		vitalsTest: VitalsTestResultDTO
	) => {
		if (assessmentResult.vitalsScore && vitalsTest.id!) {
			const vitalsLocation = props.locations.find(
				(loc) => loc.id === vitalsTest.locationId
			);
			const vitalsLocationText = vitalsLocation ? vitalsLocation.name : "";
			return (
				<div className="container full-width">
					<table className="mb-2 full-width">
						<tbody>
							<tr>
								<td className="full-width" colSpan={2}>
									<h1
										className="score-header"
										style={{ color: secondaryColor }}
									>
										{vitalsReplacementText}
										<span className="score-header-text">
											&nbsp;Administered By {props.report.vitalsEvaluator} on&nbsp;
											{moment.utc(vitalsTestResult.dateScored).local().format(
												"MMM DD, YYYY"
											)}
											&nbsp;at {vitalsLocationText}
										</span>
									</h1>
								</td>
							</tr>
							<tr>
								<td className="score-td-size">
									<div
										className="core-score-board"
										style={{ backgroundColor: primaryColor }}
									>
										<h1 className="core-score-text">
											{assessmentResult.vitalsScore}%
										</h1>
									</div>
								</td>
								<td className="d-flex align-items-center ml-2 align-text-helper">
									<div>
										<b>Recommendation: </b>
										{generateRecommendation(
											report.vitalsStatusId!,
											"vitalsStatusId"
										)}
									</div>
								</td>
							</tr>
						</tbody>
					</table>
					<h1 className="vital-block-header">TASK COMPLETION STATUS</h1>
					<table className="full-width" cellSpacing={0} cellPadding={0}>
						<tbody>
							<tr className="full-width">
								<td className="infoWrapper1" vertical-align="top">
									<table
										className="performance-summary-table full-width"
										cellSpacing={0}
										cellPadding={2}
									>
										<tbody>
											<tr>
												<td className="performance-summary-td performance-summary-center">
													Task 1
												</td>
												<td className="performance-summary-td performance-summary-center">
													Task 2
												</td>
												<td className="performance-summary-td performance-summary-center">
													Task 3
												</td>
												<td className="performance-summary-td performance-summary-center">
													Task 4
												</td>
												<td className="performance-summary-td performance-summary-center">
													Task 5
												</td>
												<td className="performance-summary-td performance-summary-center">
													Task 6
												</td>
												<td className="legend-text legendTD">Legend</td>
											</tr>
											<tr>
												<td className="performance-summary-td performance-summary-center">
													Reactions and
													<br /> Control
												</td>
												<td className="performance-summary-td performance-summary-center">
													Focus and
													<br /> Attention
												</td>
												<td className="performance-summary-td performance-summary-center">
													Spatial
													<br />
													Judgement
												</td>
												<td className="performance-summary-td performance-summary-center">
													Attention
													<br />
													Shifting
												</td>
												<td className="performance-summary-td performance-summary-center">
													Memory and
													<br /> Control
												</td>
												<td className="performance-summary-td performance-summary-center">
													Driving
													<br />
													Situations
												</td>
												<td
													style={{ paddingLeft: "5px" }}
													className="legendTD"
													rowSpan={2}
												>
													{/* figure out icons to render instead of images */}
													<Icon
														icon={Circle}
														filled
														size="2x"
														style={{ color: "#ffffff" }}
													/>
													&nbsp;
													<span className="legend-text">Completed</span> <br />
													<Icon
														icon={HalfCircle}
														size="2x"
														style={{ color: "#ffffff" }}
													/>
													&nbsp;
													<span className="legend-text">
														Partially Completed
													</span>
													<br />
													<Icon
														icon={Circle}
														size="2x"
														style={{ color: "#ffffff" }}
													/>
													&nbsp;
													<span className="legend-text">Not Completed </span>
												</td>
											</tr>
											<tr>{renderCompletedTaskIcons(vitalsCompletedTasks)}</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
					<h1 className="vital-block-header">SKILLS BREAKDOWN</h1>
					<div className="assessment-container">
						<table>
							<tbody>
								<tr>
									<td className="congnitive-container td-align-top">
										<h4 text-align="left" style={{ color: primaryColor }}>
											COGNITIVE FUNCTIONS ASSESSED
										</h4>
										<p>
											Below/above average performance on one or more of the
											tasks does not directly correlate to driving ability and
											should not be used to make decisions about licensing
											status.
										</p>
										<ul className="list-unstyled">
											<li className="inline-l">
												{renderIconForReactions(vitalsSubScores)}{" "}
												&nbsp;Initialization and reactions
											</li>
											<li className="inline-l">
												{renderIconForProcessVisualEvents(vitalsSubScores)}
												&nbsp; Track and process visual events
											</li>
											<li className="inline-l">
												{renderIconForResponseToStimuli(vitalsSubScores)}
												&nbsp; Ability to encode, retrieve and/or respond to
												stimuli
											</li>
											<li className="inline-l">
												{renderIconForSpatialRelations(vitalsSubScores)}
												&nbsp; Judgement of spatial relations
											</li>
											<li className="inline-l">
												{renderIconForDecisionTimePressure(vitalsSubScores)}
												&nbsp; Decisions under time pressure
											</li>
											<li className="inline-l">
												{renderIconForGuidedMovementControl(vitalsSubScores)}
												&nbsp; Guided movement control
											</li>
											<li className="inline-l">
												{renderIconForComplexInformation(vitalsSubScores)}
												&nbsp; Ability to quickly respond to complex information
											</li>
											<li className="inline-l">
												{renderIconForFineMotorControl(vitalsSubScores)}{" "}
												&nbsp;Fine motor control
											</li>
											<li className="inline-l">
												{renderIconForImpluseControl(vitalsSubScores)}{" "}
												&nbsp;Impulse Control
											</li>
										</ul>
									</td>
									<td className="congnitive-legend td-align-top">
										<h4 text-align="left" style={{ color: primaryColor }}>
											Legend
										</h4>
										<ul className="list-unstyled">
											<li>
												<div className="red-arrow-down" />
												&nbsp;
												<p className="inline-p"> Below Average Performance</p>
											</li>
											<li>
												<div className="black-square" />
												&nbsp;
												<p className="inline-p"> Average Performance</p>
											</li>
											<li>
												<div className="green-arrow-up" />
												&nbsp;
												<p className="inline-p"> Above Average Performance</p>
											</li>
										</ul>
									</td>
									<td className="assessment-factors td-align-top">
										<h4 text-align="left" style={{ color: primaryColor }}>
											ASSESSMENT FACTORS
										</h4>
										<ul className="list-unstyled">
											<li>
												Cooperative:&nbsp;
												{getPickListItemDescription(vitalsTest.cooperativeId)}
											</li>
											<li>
												Physical:&nbsp;
												{getPickListItemDescription(
													vitalsTest.physicalImpairmentId
												)}
											</li>
											<li>
												Vision:&nbsp;
												{getPickListItemDescription(
													vitalsTest.visualImpairmentId
												)}
											</li>
											<li>
												Hearing:&nbsp;
												{getPickListItemDescription(
													vitalsTest.hearingImpairmentId
												)}
											</li>
											<li>
												Language:&nbsp;
												{getPickListItemDescription(
													vitalsTest.languageComprehensionId
												)}
											</li>
											<li>
												Anxiety:&nbsp;
												{getPickListItemDescription(vitalsTest.anxietyLevelId)}
											</li>
										</ul>
									</td>
								</tr>
							</tbody>
						</table>
						<table cellSpacing={0} cellPadding={0} className="full-width">
							<tbody>
								<tr>
									<td className="td-align-top text-break">
										<h4 text-align="left" style={{ color: primaryColor }}>
											ASSESSMENT COMMENTS
										</h4>
										<p className="keep-white-space">
											{vitalsTest.comments === ""
												? "None"
												: vitalsTest.comments}
										</p>
									</td>
								</tr>
							</tbody>
						</table>
						{generateVitalsCustomFieldRows()}
					</div>
					<hr />
				</div>
			);
		} else {
			return;
		}
	};

	const renderCoreSection = (
		assessmentReport: ReportNecessaryInfoDTO,
		coreTest: CoreTestResultDTO
	) => {
		if (coreTestResult.dateCompleted! && coreTestResult.id!) {
			const coreLocation = props.locations.find(
				(loc) => loc.id === coreTest.locationId
			);
			const coreLocationText = coreLocation ? coreLocation.name : "";
			const coreCourseText = report.courseName
				? " on " + report.courseName
				: "";
			return (
				<div className="container full-width">
					<div className="guarantee-page-break">
						<table className="mb-2 full-width guarantee-page-break">
							<tbody>
								<tr>
									<td className="full-width" colSpan={2}>
										<h1
											className="score-header"
											style={{ color: secondaryColor }}
										>
											{coreReplacementText}
											<span className="score-header-text">
												&nbsp;
												<img
													src={
														getPickListItemDescription(
															coreTestResult.vehicleTypeId
														) === "Heavy" ||
														getPickListItemDescription(
															coreTestResult.vehicleTypeId
														) === "Trucking"
															? largeVehicle
															: lightVehicle
													}
													className="vehicle-icon"
												/>
												&nbsp;Administered By {props.report.coreEvaluator} on&nbsp;
												{moment.utc(coreTestResult.dateCompleted).local().format(
													"MMM DD, YYYY"
												)}
												&nbsp;at {coreLocationText}
												{coreCourseText}
											</span>
										</h1>
									</td>
								</tr>
								{showCoreScore(assessmentReport.coreScore)}
							</tbody>
						</table>
						<table className="full-width guarantee-page-break">
							<tbody>
								<tr>
									<td
										className="core-row core-row-hd"
										align="center"
										text-align="center"
										style={{ backgroundColor: primaryColor }}
									>
										Assessment Category
									</td>
									<td
										className="core-row core-row-hd"
										align="center"
										colSpan={2}
										style={{ backgroundColor: primaryColor }}
									>
										Result
									</td>
									<td
										className="core-row core-row-hd pl-2"
										colSpan={3}
										style={{ backgroundColor: primaryColor }}
									>
										Recommendations
									</td>
								</tr>
								<tr>
									<td
										className="core-row core-row-padding"
										style={{ padding: "20px" }}
									>
										<strong>Competence Defining Errors</strong>
									</td>
									<td className="core-row" colSpan={2} align="center">
										{generateIconForErrors(report.cErrorStatusId!)}
									</td>
									<td className="core-row" colSpan={3}>
										<p>
											{generateCategoryDescription(
												report.cErrorStatusId!,
												"cErrorStatusId"
											)}
										</p>
										<br />
										<b>Recommendation: </b>
										{generateRecommendation(
											report.cErrorStatusId!,
											"cErrorStatusId"
										)}
									</td>
								</tr>
								<tr>
									<td className="core-row" colSpan={3} rowSpan={2}>
										&nbsp;
									</td>
									<td className="core-row" text-align="center">
										{setHazardousErrorIcon(report.cErrorStatusId!)}
									</td>
									<td className="core-row">Hazardous Errors</td>
									<td className="core-row">
										These errors represent an extreme danger to the driver,
										other road users and road evaluators and in most cases would
										lead to a collision without intervention.
									</td>
								</tr>
								<tr>
									<td className="core-row" text-align="center">
										{setAbortedErrorIcon(report.coreAborted!)}
									</td>
									<td className="core-row">Aborts</td>
									<td className="core-row">
										The number of errors may be low due to the on-road
										evaluation having to be aborted by the evaluator due to
										safety reasons.
									</td>
								</tr>
								<tr>
									<td className="core-row" align="center">
										<strong>Trainable Errors</strong>
									</td>
									<td className="core-row" colSpan={2} align="center">
										{generateIconForErrors(report.tErrorStatusId!)}
									</td>
									<td className="core-row" colSpan={3}>
										<p>
											{generateCategoryDescription(
												report.tErrorStatusId!,
												"tErrorStatusId"
											)}
										</p>
										<br />
										<b>Recommendation: </b>
										{generateRecommendation(
											report.tErrorStatusId!,
											"tErrorStatusId"
										)}
									</td>
								</tr>
								<tr>
									<td className="core-row" align="center">
										<strong>Skills Appraisal</strong>
									</td>
									<td className="core-row" colSpan={2} align="center">
										{generateIconForErrors(report.appraisalStatusId!)}
									</td>
									<td className="core-row" colSpan={3}>
										<p>
											{generateCategoryDescription(
												report.appraisalStatusId!,
												"appraisalStatusId"
											)}
										</p>
										<br />
										<b>Recommendation: </b>
										{generateRecommendation(
											report.appraisalStatusId!,
											"appraisalStatusId"
										)}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<br />
					<table cellSpacing={0} cellPadding={0} className="full-width">
						<tbody>
							<tr>
								<td className="core-grey-block td-align-top">
									<h4
										className="core-grey-block-header"
										style={{ color: primaryColor }}
									>
										DRIVING SKILL SUMMARY
									</h4>
									<p>
										Listed below are the specific driving categories where the
										driver exhibited difficulties.
									</p>
									{generateSkillSummary()}
								</td>
								<td className="core-grey-block td-align-top">
									<h4
										className="core-grey-block-header"
										style={{ color: primaryColor }}
									>
										ASSESSMENT FACTORS
									</h4>
									<ul>
										<li>
											Cooperative:&nbsp;
											{getPickListItemDescription(coreTestResult.cooperativeId)}
										</li>
										<li>
											Physical:&nbsp;
											{getPickListItemDescription(
												coreTestResult.physicalImpairmentId
											)}
										</li>
										<li>
											Vision:&nbsp;
											{getPickListItemDescription(
												coreTestResult.visualImpairmentId
											)}
										</li>
										<li>
											Hearing:&nbsp;
											{getPickListItemDescription(
												coreTestResult.hearingImpairmentId
											)}
										</li>
										<li>
											Anxiety:&nbsp;
											{getPickListItemDescription(
												coreTestResult.anxietyLevelId
											)}
										</li>
									</ul>
								</td>
							</tr>
						</tbody>
					</table>
					<table
						className="full-width guarantee-page-break"
						cellSpacing={0}
						cellPadding={0}
					>
						<tbody>
							<tr>
								<td className="core-grey-block">
									<h4
										className="core-grey-block-header"
										style={{ color: primaryColor }}
									>
										DRIVING ERROR SUMMARY
									</h4>
									<p>
										The following driving errors were identified on the road.
										These errors are listed only as a guide in helping to
										determine specific areas that may have deficits.
									</p>
									{generateErrorSummary()}
								</td>
							</tr>
						</tbody>
					</table>
					<table cellSpacing={0} cellPadding={0} className="full-width">
						<tbody>
							<tr>
								<td className="core-grey-block text-break">
									<h4
										className="core-grey-block-header"
										style={{ color: primaryColor }}
									>
										ASSESSMENT COMMENTS
									</h4>
									<p className="keep-white-space">
										{coreTestResult.comments === ""
											? "None"
											: coreTestResult.comments}
									</p>
								</td>
							</tr>
						</tbody>
					</table>
					{generateCoreCustomFieldRows()}
					<hr />
				</div>
			);
		} else {
			return;
		}
	};

	const renderDisclaimer = (discl?: string) => {
		let disclaimerTitle = (
			<React.Fragment>
				<h1
					className="disclaimer-header-text"
					style={{ color: secondaryColor }}
				>
					DISCLAIMER
				</h1>
			</React.Fragment>
		);

		if (discl) {
			return (
				<React.Fragment>
					<div className="guarantee-page-break">
						{disclaimerTitle}
						{discl}
					</div>
				</React.Fragment>
			);
		} else {
			return (
				<React.Fragment>
					<div className="guarantee-page-break">
						{disclaimerTitle}
						<p>
							The ExceleRATE assessment procedures are intended to be used for
							the evaluation of an experienced driver's competency to drive. It
							is not intended for novice drivers, nor is the evaluation intended
							for participants for whom the basis for questioning competence is
							the presence of an illness with sporadic compromising episodes.
							Neither the ExceleRATE evaluation procedures nor any other
							procedures can guarantee that a person will drive safely or be
							free of vehicle crashes. Performance resulting in any score
							indicates only that the participant has a greater or lesser
							probability of performing the type or number/severity of driving
							errors during a specialized on-road evaluation. No other claim is
							made or implied. The participant may have poor driving habits,
							which, if ameliorated, could improve driving safety. If the
							participant has a progressive illness, acquires one or more new
							illnesses or changes medications, competence may change. In such
							cases, the outcome of the current evaluation may no longer be
							valid and re-evaluation would be warranted.
						</p>
						<p>
							Impirica Inc. makes recommendations based on the performance on
							the ExceleRATE assessment procedures and in no way makes, controls
							or legislates driver licensing decisions and further does not
							control the reporting or recommendations made by recipients of the
							report nor does it control the reliance or extent to which the
							results may have affected medical or licensing decisions.
						</p>
					</div>
				</React.Fragment>
			);
		}
	};

	return pickListItems.length > 0 ? (
		<div className="container">
			<table className="mb-4 full-width" cellSpacing={0} cellPadding={0}>
				<tbody>
					<tr>
						<td className="td-col-4" align="center">
							<img
								src={logoUrl || impiricaLogo}
								className="scale-bar"
								alt="ExceleRATE"
							/>
						</td>
						<td className="td-col-6 evaluator-form" vertical-align="top">
							<table
								className="evaluator-form-table"
								cellSpacing={0}
								cellPadding={0}
							>
								<tbody>
									<tr>
										<td
											className="evaluator-form-header core-row"
											colSpan={4}
											style={{ backgroundColor: secondaryColor }}
										>
											{excelerateReplacementText || "ExceleRATE"} DRIVER
											EVALUATION REPORT
										</td>
									</tr>
									{renderClientSection(report)}
								</tbody>
							</table>
						</td>
					</tr>
				</tbody>
			</table>
			{/* Full test result */}
			{renderExcelerateSection(report)}
			{/* Vital test result - vitalsTestResult.id exists means currentUser has accessability to that test*/}
			{renderVitalsSection(report, vitalsTestResult)}
			{/* Core test result - coreTestResult.id exists means currentUser has accessability to that test*/}
			{renderCoreSection(report, coreTestResult)}
			<div className="mt-2 container disclaimerContainer">
				<div>
					<div>{renderDisclaimer(disclaimer)}</div>
				</div>
			</div>
		</div>
	) : null;
};

export default FullReportTemplate;
